import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Select, { components } from 'react-select';
import { Col, Row, NavItem } from 'reactstrap';

import { IconButton, tcBuildSessionURL, SCOPES } from '@techchange/components';
import { messages } from '../../../i18n';

import usePaginatedSearch from '../hooks/usePaginatedSearch';

import NavSearchResultsOption from './NavSearchResultsOption';

import './NavSearch.scss';

const DROPDOWN_MENU_ID = 'nav-searchbar-dropdown-menu';

const propTypes = {

	/**
	 * The current user.
	 */
	currentUser: PropTypes.shape({

		/**
		 * The user's ID.
		 */
		id: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * True, if the current user has admin privileges for the organization.
	 */
	isAdmin: PropTypes.bool,

};

/* eslint-disable-next-line react/prop-types */
const CustomMenuList = ({ children, innerProps, ...otherProps }) => (
	<components.MenuList
		{...otherProps}
		innerProps={{
			...innerProps,
			tabIndex: -1,
		}}>
		{children}
	</components.MenuList>
);

const NavSearch = ({ currentUser, isAdmin }) => {

	const intl = useIntl();

	const searchInputRef = useRef(null);

	const paginatedSearchParams = {
		active: true,
		user_id: currentUser.id,
		include: [SCOPES.COURSE, SCOPES.SESSION],
	};

	// Only show open sessions unless the user is an admin
	if (!isAdmin) {
		paginatedSearchParams.session_is_open = true;
	}

	const [
		searchState,
		loadState,
		onSearchChange,
		onSearchFocus,
		onSearchReset,
	] = usePaginatedSearch(paginatedSearchParams, DROPDOWN_MENU_ID);

	// Auto-focus the search input once the searchbar changes from hidden to visible (for mobile only)
	useEffect(() => {

		if (searchState.isSearching && searchInputRef) {
			searchInputRef.current.focus();
		}

	}, [searchState.isSearching]);

	const loadingMessage = () => {

		return (
			<div>
				<FormattedMessage
					{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_DROPDOWN_MENU_TEXT_LOADING_OPTIONS} />
			</div>
		);
	};

	const noOptionsMessage = () => {

		// Check if there was an error during the search
		if (searchState.error) {
			return (
				<Row className="flex-column g-0">
					<strong
						className="mb-3 col"
						aria-live="polite">
						<FormattedMessage
							{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_DROPDOWN_MENU_TEXT_ERROR_HEADER} />
					</strong>
					<Col>
						<FormattedMessage
							{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_DROPDOWN_MENU_TEXT_ERROR_MSG}
							values={{
								link: (
									<a
										tabIndex="-1"
										href="/dashboard">
										<FormattedMessage
											{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TEXT_LINK_TO_HERE} />
									</a>
								)
							}} />
					</Col>
				</Row>
			);
		}

		// Check if still waiting for the search
		if (!searchState.results) {
			return (
				<Row className="g-0">
					<FormattedMessage
						className="col"
						{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_DROPDOWN_MENU_TEXT_LOADING_OPTIONS} />
				</Row>
			);
		}

		// Check if the empty search has no results
		if (searchState.results.length === 0 && loadState.loadedAll && !searchState.searchText) {

			return (
				<Row className="g-0">
					<FormattedMessage
						className="col"
						aria-live="polite"
						{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_DROPDOWN_MENU_TEXT_NO_OPTIONS}
						values={{
							searchText: searchState.searchText,
						}} />
				</Row>
			);
		}

		// Default return if the search has no matching results
		return (
			<Row className="flex-column g-0">
				<Col className="mb-3">
					<FormattedMessage
						{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_DROPDOWN_MENU_TEXT_NO_MATCH}
						values={{
							searchText: searchState.searchText,
						}} />
				</Col>
				<a
					className="col"
					tabIndex="-1"
					href="/dashboard">
					<FormattedMessage
						{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_DROPDOWN_MENU_TEXT_VIEW_ENROLLMENTS} />
				</a>
			</Row>
		);

	};

	const formatOptionLabel = (value) => (
		<NavSearchResultsOption
			options={searchState.results || []}
			data={value}
			isAdmin={isAdmin} />
	);

	// Handle select option changes for keyboard selection
	const handleChange = (value) => {
		window.location.href = tcBuildSessionURL(value.session);
	};

	return (
		<NavItem id="search-nav-item">
			<IconButton
				className="d-block d-sm-none"
				title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_PLACEHOLDER_TEXT)}
				icon="fal fa-magnifying-glass fa-xl"
				size="45px"
				onClick={onSearchFocus} />
			<div className="nav-searchbar">
				<Select
					ref={searchInputRef}
					id="nav-searchbar-select"
					className={searchState.isSearching ? 'is-searching' : ''}
					styles={{
						control: (base, state) => ({
							...base,
							backgroundColor: state.isFocused
								? 'transparent'
								: 'var(--bs-gray-200)',
							border: 'none',
							boxShadow: state.isFocused
								// eslint-disable-next-line max-len
								? '0 0 0 .25rem hsla(var(--primary-color-h), var(--primary-color-s), var(--primary-color-l), 0.25)'
								: 'none',
							outline: state.isFocused
								? '2px solid var(--tc-universal-navbar-color, var(--primary-color))'
								: 'none',
							height: '100%',
						}),
						input: (base) => ({
							...base,
							color: 'var(--tc-universal-navbar-color, var(--body-color))',
						}),
						menu: (base) => ({
							...base,
							width: 'max-content',
							minWidth: '100%',
							maxWidth: '80vw',
							zIndex: '9999',
						}),
						option: (base, state) => ({
							...base,
							padding: 0,
							backgroundColor: state.isFocused
								? 'var(--bs-gray-200)'
								: '#fff',
							color: 'var(--body-color)',
						}),
						placeholder: (base, state) => ({
							...base,
							color: state.isFocused
								? 'var(--tc-universal-navbar-color, var(--body-color))'
								: 'var(--body-color)',
						}),

						noOptionsMessage: (base) => ({
							...base,
							color: 'var(--body-color)',
						}),
					}}
					isLoading={loadState.loading}
					noOptionsMessage={noOptionsMessage}
					loadingMessage={loadingMessage}
					formatOptionLabel={formatOptionLabel}
					getOptionLabel={(enrollment) => enrollment.session.title}
					getOptionValue={(enrollment) => enrollment}
					components={{
						DropdownIndicator: () => null,
						IndicatorSeparator: () => null,
						SingleValue: () => null,
						MenuList: CustomMenuList,
					}}
					menuIsOpen={searchState.isSearching}
					tabSelectsValue={false}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_PLACEHOLDER_TEXT)}
					aria-label={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_PLACEHOLDER_TEXT)}
					options={searchState.results || []}
					onChange={handleChange}
					onInputChange={(newValue, actionMeta) => {

						// Only trigger a search request if the input has changed
						if (actionMeta.action === 'input-change') {
							onSearchChange(newValue);
						}

					}}
					onBlur={onSearchReset}
					onFocus={onSearchFocus} />
			</div>
		</NavItem>
	);

};

NavSearch.propTypes = propTypes;

export default NavSearch;
